import React, { useEffect } from "react";
import { useDispatch } from "react-redux";

// Redux
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getBodyshops, resetReducerBodyshops, useBodyshopReducer } from "@/main/store/bodyshop";
import { getInsurers, resetReducerInsurers, useInsurerReducer } from "@/main/store/insurer";
import { getSuppliers, resetReducerSuppliers, useSupplierReducer } from "@/main/store/supplier";

// Hooks
import useFormQueryParams from "@/main/hooks/useformQueryParams";

// Types
import { KeyValue } from "@/main/models/common";
import { ProcurementReportResponse, SearchGridShippingCostResponse } from "@/main/models/response";
import {
	BodyShopFilterRequest,
	InsurerFilterRequest,
	ProcurementReportRequest,
	SupplierFilterRequest,
} from "@/main/models/request";

// Services
import { getDefaultOptionDrop, getTranslateByKey } from "@/main/services/utils";
import { initalDateForm, onLoadDateRangePicker } from "@/main/services/utilsDate";
import { userIsSupplier } from "@/main/services/utilsUser";
import { HeadQuarterBranchFilterEnum } from "@/main/services/enum/HeadQuarterBranchFilterEnum";
import { StatusRegisterEnum } from "@/main/services/enum/StatusRegisterEnum";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";

// Componentes
import Box from "@/main/components/shared/box";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import KogKeyboardDatePickerContainer from "@/main/components/shared/datepicker/keyboardDatePickerContainer";
import Button from "@/main/components/shared/button/button";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import { AreaButton } from "@/main/components/shared/styles";

// Styles
import * as S from "./styles";

export interface IGridFiltersData {
	data: (SearchGridShippingCostResponse | ProcurementReportResponse)[];
	totalItems: number;
	isLoading: boolean;
}

export const INITIAL_GRID_FILTERS_STATE: IGridFiltersData = {
	totalItems: 0,
	isLoading: false,
	data: [],
};

export type IReportFiltersForm = {
	country: KeyValue | null;
	bodyshopBranch: KeyValue | null;
	insurer: KeyValue | null;
	supplier: KeyValue | null;
	startDate: Date;
	finalDate: Date;
	claimNr: string;
};

interface IBoxFiltersComponentes {
	loadingExportOrder: boolean;
	showFilters: boolean;
	setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
	loadingSave: boolean;
	setGridData: React.Dispatch<React.SetStateAction<IGridFiltersData>>;
	setIsLoadingInfiniteScroll: React.Dispatch<React.SetStateAction<boolean>>;
	setHasShowMore: React.Dispatch<React.SetStateAction<boolean>>;
	setPageIndex: React.Dispatch<React.SetStateAction<number>>;
	setFormFilters: React.Dispatch<ProcurementReportRequest | null>;
	onSubmitExportOrder: (dataForm: IReportFiltersForm) => void;
	onSubmitSearch: (dataForm: IReportFiltersForm) => void;
	parameterRangeDays: number;
}

function BoxFiltersComponentes({
	loadingExportOrder,
	showFilters,
	setShowFilters,
	loadingSave,
	setGridData,
	setIsLoadingInfiniteScroll,
	setHasShowMore,
	setPageIndex,
	setFormFilters,
	onSubmitExportOrder,
	onSubmitSearch,
	parameterRangeDays,
}: IBoxFiltersComponentes) {
	const dispatch = useDispatch();
	const countryReducer = useCountryReducer();
	const bodyshopReducer = useBodyshopReducer();
	const insurerReducer = useInsurerReducer();
	const supplierReducer = useSupplierReducer();
	const optionDefaultCountryDrop = getDefaultOptionDrop(countryReducer.countriesUser.data);
	const optionDefaultSupplierDrop = getDefaultOptionDrop(supplierReducer.suppliers.data);

	const isDisabledInsurersDrop = () => insurerReducer.insurers.data === null;
	const isDisabledBodyshopDrop = () => bodyshopReducer.bodyshops.data === null;
	const isDisabledSupplierDrop = () => supplierReducer.suppliers.data === null || userIsSupplier();

	const INITIAL_FORM_STATE = {
		country: null as KeyValue | null,
		bodyshopBranch: null as KeyValue | null,
		insurer: null as KeyValue | null,
		supplier: null as KeyValue | null,
		startDate: initalDateForm(parameterRangeDays),
		finalDate: initalDateForm(),
		claimNr: "" as string,
	};

	const {
		control,
		customHandleSubmit,
		setValue,
		formState: { errors },
		reset,
		getValues,
		clearErrors,
		watch,
	} = useFormQueryParams<IReportFiltersForm>({
		mode: "all",
		defaultValues: INITIAL_FORM_STATE,
		onSubmit: onSubmitSearch,
		mapDefaultValues: {
			bodyshopBranch: bodyshopReducer.bodyshops,
			country: countryReducer.countriesUser,
			insurer: insurerReducer.insurers,
			supplier: supplierReducer.suppliers,
		},
	});

	// Watchers
	const watcherCountry = watch("country");

	function callInsurerApiDomain(companySelected: KeyValue | null = null, countrySelected: KeyValue | null = null) {
		// Clean fields & reducers
		dispatch(resetReducerInsurers());

		if (countrySelected !== null || optionDefaultCountryDrop !== null) {
			const request: InsurerFilterRequest = {
				...(companySelected && { guidBodyShop: String(companySelected.key) }),
				guidCountry: optionDefaultCountryDrop?.key
					? String(optionDefaultCountryDrop?.key)
					: String(countrySelected?.key),
				type: HeadQuarterBranchFilterEnum.All,
				idStatusRegister: StatusRegisterEnum.Active,
			};

			dispatch(getInsurers(request));
		}
	}

	function callBodyshopApiDomain(insurerSelected: KeyValue | null = null, countrySelected: KeyValue | null = null) {
		// Clean fields & reducers
		dispatch(resetReducerBodyshops());

		if (countrySelected !== null || optionDefaultCountryDrop !== null) {
			const request: BodyShopFilterRequest = {
				...(insurerSelected && { guidInsurer: String(insurerSelected.key) }),
				guidCountry: optionDefaultCountryDrop?.key
					? String(optionDefaultCountryDrop?.key)
					: String(countrySelected?.key),
				type: HeadQuarterBranchFilterEnum.All,
				idStatusRegister: StatusRegisterEnum.Active,
			};
			dispatch(getBodyshops(request));
		}
	}

	function callSupplierApiDomain(optionSelected: KeyValue | null) {
		// Clean fields & reducers
		setValue("supplier", null);
		dispatch(resetReducerSuppliers());

		if (optionSelected !== null) {
			const request: SupplierFilterRequest = {
				guidCountry: String(optionSelected.key),
				type: HeadQuarterBranchFilterEnum.All,
				idStatusRegister: StatusRegisterEnum.Active,
			};
			dispatch(getSuppliers(request));
		}
	}

	function handleClickClear() {
		reset(INITIAL_FORM_STATE);
		setFormFilters(null);

		setPageIndex(PaginationEnum.PageIndexIntial);
		setHasShowMore(false);
		setIsLoadingInfiniteScroll(false);

		if (optionDefaultCountryDrop) setValue("country", optionDefaultCountryDrop);

		if (optionDefaultCountryDrop !== null) {
			callBodyshopApiDomain();
			callInsurerApiDomain();
			callSupplierApiDomain(optionDefaultCountryDrop);
		} else {
			dispatch(resetReducerBodyshops());
			dispatch(resetReducerInsurers());
			dispatch(resetReducerSuppliers());
		}

		clearErrors();
		const { firstDateResult, secondDateResult } = onLoadDateRangePicker(parameterRangeDays);
		setValue("startDate", firstDateResult);
		setValue("finalDate", secondDateResult);

		setGridData(INITIAL_GRID_FILTERS_STATE);
	}

	useEffect(() => {
		if (optionDefaultCountryDrop) {
			callInsurerApiDomain(null, watcherCountry);
			callBodyshopApiDomain(null, watcherCountry);
			callSupplierApiDomain(optionDefaultCountryDrop);
		}
	}, [optionDefaultCountryDrop?.key]);

	useEffect(() => {
		dispatch(getCountriesUser());

		return () => {
			dispatch(resetReducerBodyshops());
			dispatch(resetReducerInsurers());
			dispatch(resetReducerSuppliers());
		};
	}, []);

	function handleFilters() {
		setShowFilters(!showFilters);
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	}

	return (
		<>
			<AreaButton>
				<Button
					onClick={() => onSubmitExportOrder(getValues())}
					id="id-filtersReport-button-exportExcel"
					isLoading={loadingExportOrder}
				>
					{getTranslateByKey("information.exportExcel")}
				</Button>
				<Button onClick={handleFilters} id="id-filtersReport-button-filters">
					{getTranslateByKey("information.filters")}
				</Button>
			</AreaButton>
			<Box showBox={showFilters} title={getTranslateByKey("information.filters")}>
				<form onSubmit={customHandleSubmit} noValidate>
					<S.RowInputs>
						<KogAutoCompleteControlled
							id="id-filtersReport-autoComplete-country"
							nameCustom="country"
							label={getTranslateByKey("information.country")}
							options={countryReducer.countriesUser.data}
							isLoading={countryReducer.countriesUser.isLoading}
							rulesCustom={{ required: true }}
							errorMessageCustom={
								errors.country?.type === "required" ? getTranslateByKey("information.countryRequired", true) : ""
							}
							setValueCustom={setValue}
							disabled={countryReducer.countriesUser.data == null || optionDefaultCountryDrop != null}
							control={control}
							onChangeCustom={(optionSelected: KeyValue | null) => {
								callBodyshopApiDomain(null, optionSelected);
								callInsurerApiDomain(null, optionSelected);
								callSupplierApiDomain(optionSelected);
							}}
							isDefaultValueUnique
						/>

						<KogAutoCompleteControlled
							nameCustom="insurer"
							id="id-filtersReport-select-insurer"
							setValueCustom={setValue}
							label={getTranslateByKey("information.insurer")}
							control={control}
							rulesCustom={{ required: false }}
							errorMessageCustom=""
							hasErrorCustom={false}
							options={insurerReducer.insurers.data}
							isLoading={insurerReducer.insurers.isLoading}
							disabled={isDisabledInsurersDrop()}
							onChangeCustom={(optionSelected: KeyValue | null) => {
								if (optionSelected !== null) {
									callBodyshopApiDomain(optionSelected, watcherCountry);
								} else {
									callInsurerApiDomain(null, watcherCountry);
									callBodyshopApiDomain(null, watcherCountry);
								}
							}}
						/>
						<KogAutoCompleteControlled
							nameCustom="bodyshopBranch"
							id="id-filtersReport-select-bodyshopBranch"
							setValueCustom={setValue}
							label={getTranslateByKey("information.bodyshopBranch")}
							control={control}
							rulesCustom={{ required: false }}
							options={bodyshopReducer.bodyshops.data}
							isLoading={bodyshopReducer.bodyshops.isLoading}
							disabled={isDisabledBodyshopDrop()}
							onChangeCustom={(optionSelected: KeyValue | null) => {
								if (optionSelected !== null) {
									callInsurerApiDomain(optionSelected, watcherCountry);
								} else {
									callBodyshopApiDomain(null, watcherCountry);
									callInsurerApiDomain(null, watcherCountry);
								}
							}}
						/>
					</S.RowInputs>
					<S.RowInputs>
						<KogAutoCompleteControlled
							label={getTranslateByKey("information.supplier")}
							control={control}
							nameCustom="supplier"
							isDefaultValueUnique={userIsSupplier() && optionDefaultSupplierDrop !== null}
							rulesCustom={{ required: false }}
							errorMessageCustom=""
							hasErrorCustom={false}
							options={supplierReducer.suppliers.data}
							isLoading={supplierReducer.suppliers.isLoading}
							onChangeCustom={() => {}}
							disabled={isDisabledSupplierDrop()}
							autoHighlightCustom={!!optionDefaultSupplierDrop}
							setValueCustom={setValue}
							id="id-filtersReport-input-supplier"
						/>

						<KogKeyboardDatePickerContainer
							idScreen="id-filtersReport"
							nameStartDate="startDate"
							nameFinalDate="finalDate"
							parameterRangeDays={parameterRangeDays}
							control={control}
						/>

						<KogInputTextControlledNew
							name="claimNr"
							control={control}
							label={getTranslateByKey("information.claimNr")}
						/>
					</S.RowInputs>

					<S.AreaButton>
						<SecondaryButton id="id-filtersReport-button-search" type="submit" isLoading={loadingSave}>
							{getTranslateByKey("information.search")}
						</SecondaryButton>
						<SecondaryButton id="id-filtersReport-button-clear" onClick={handleClickClear}>
							{getTranslateByKey("information.clear")}
						</SecondaryButton>
					</S.AreaButton>
				</form>
			</Box>
		</>
	);
}

export default BoxFiltersComponentes;
